import { useState, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import OffersBlock from "@/components/seo/OffersBlock";
import RecentPagesBlock from "@/components/seo/RecentPagesBlock";
import PopularPagesBlock from "@/components/seo/PopularPagesBlock";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Search, Tv, Star, TrendingUp } from "lucide-react";
import heroSeries from "@/assets/hero-hub-series.webp";
import { Input } from "@/components/ui/input";

interface TmdbResult {
  title: string;
  slug: string;
  poster_url: string | null;
  release_date: string | null;
  is_released: boolean;
}

const SeriesHub = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
    if (debounceTimer) clearTimeout(debounceTimer);
    const timer = setTimeout(() => setDebouncedQuery(value), 400);
    setDebounceTimer(timer);
  }, [debounceTimer]);

  // Local DB series
  const { data: series } = useQuery({
    queryKey: ["series-hub"],
    queryFn: async () => {
      const { data } = await supabase
        .from("series")
        .select("*")
        .eq("published", true)
        .order("air_date", { ascending: false })
        .limit(500);
      return data || [];
    },
  });

  // TMDB search results
  const { data: tmdbResults, isFetching: isSearching } = useQuery({
    queryKey: ["tmdb-search-series", debouncedQuery],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("search-tmdb", {
        body: { query: debouncedQuery, type: "series" },
      });
      if (error) throw error;
      return (data?.results as TmdbResult[]) || [];
    },
    enabled: debouncedQuery.length >= 2,
  });

  const displaySeries = useMemo(() => {
    if (debouncedQuery.length >= 2 && tmdbResults) {
      return tmdbResults.map((r) => ({
        id: r.slug,
        title: r.title,
        slug: r.slug,
        poster_url: r.poster_url,
        air_date: r.release_date,
        season_number: null as number | null,
        isReleased: r.is_released,
      }));
    }
    if (!series) return [];
    const q = searchQuery.toLowerCase().trim();
    const filtered = q ? series.filter((s) => s.title.toLowerCase().includes(q)) : series;
    return filtered.map((s) => ({
      id: s.id,
      title: s.title,
      slug: s.slug,
      poster_url: s.poster_url,
      air_date: s.air_date,
      season_number: s.season_number,
      isReleased: s.air_date ? new Date(s.air_date) <= new Date() : true,
    }));
  }, [series, searchQuery, debouncedQuery, tmdbResults]);

  return (
    <>
      <Helmet>
        <title>Séries en Streaming VF HD - Catalogue Complet | RoiDuStreaming</title>
        <meta name="description" content="Regardez +25 000 séries en streaming VF HD et 4K. Toutes les saisons complètes sur RoiDuStreaming." />
        <link rel="canonical" href="https://roidustreaming.fr/series" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        {/* Hero with visual impact */}
        <section className="relative py-20 md:py-32 px-4 overflow-hidden">
          <img src={heroSeries} alt="Séries en streaming VF HD" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-background/70 backdrop-blur-sm" />
          
          <div className="relative z-10 max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-semibold mb-6">
              <TrendingUp className="w-4 h-4" />
              +25 000 séries disponibles
            </div>
            <h1 className="text-4xl md:text-6xl font-black mb-4 text-foreground leading-tight">
              Séries en <span className="text-primary">Streaming</span> VF
              <br />
              <span className="text-2xl md:text-4xl font-bold text-muted-foreground">HD & 4K • Saisons complètes • Sans limite</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-8">
              De Game of Thrones à Squid Game, toutes vos séries préférées en un seul abonnement.
            </p>
          </div>

          <div className="relative z-20 max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Rechercher une série (ex: Breaking Bad, Naruto, The Last of Us...)"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-12 py-7 text-lg bg-card/80 backdrop-blur-sm border-primary/30 focus:border-primary rounded-2xl shadow-lg shadow-primary/5"
              />
              {isSearching && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="py-12 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold mb-6 text-foreground">
              {debouncedQuery.length >= 2
                ? `Résultats pour "${debouncedQuery}"`
                : "Séries populaires en streaming"}
            </h2>
            {displaySeries.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {displaySeries.map((s) => (
                  <Link key={s.id} to={`/series/${s.slug}`} title={`${s.title} streaming VF HD`}>
                    <Card className="overflow-hidden bg-card border-primary/20 hover:border-primary/50 transition-all hover:scale-[1.02] group">
                      {s.poster_url ? (
                        <div className="relative">
                          <img
                            src={s.poster_url}
                            alt={`${s.title} streaming VF`}
                            className="w-full aspect-[2/3] object-cover"
                            loading="lazy"
                          />
                          <span className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-bold ${s.isReleased ? "bg-secondary text-secondary-foreground" : "bg-gold text-gold-foreground"}`}>
                            {s.isReleased ? "Disponible" : "À venir"}
                          </span>
                        </div>
                      ) : (
                        <div className="w-full aspect-[2/3] bg-muted flex items-center justify-center">
                          <Tv className="w-12 h-12 text-muted-foreground" />
                        </div>
                      )}
                      <div className="p-3">
                        <h3 className="font-bold text-foreground text-sm truncate group-hover:text-primary transition-colors">{s.title}<span className="sr-only"> streaming VF</span></h3>
                        {s.season_number && (
                          <p className="text-xs text-muted-foreground">Saison {s.season_number}</p>
                        )}
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-12">
                {searchQuery ? "Aucune série trouvée. Essayez un autre titre." : "Catalogue en cours de mise à jour."}
              </p>
            )}
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-4">
          <OffersBlock />
        </div>

        <PopularPagesBlock />
        <RecentPagesBlock />

        <Footer />
      </div>
    </>
  );
};

export default SeriesHub;
